import { ArrowLeft, Check, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SupportedLocale } from "../../lib/locale-preferences";
import { useShellLocaleStore } from "../../state/shell-locale-store";

type LocaleOption = {
  value: SupportedLocale;
  detail: string;
};

const LANGUAGE_OPTIONS: readonly LocaleOption[] = [
  {
    value: "es",
    detail: "ES",
  },
  {
    value: "en",
    detail: "EN",
  },
];

interface ShellLanguageSwitcherProps {
  onBack: () => void;
  onClose: () => void;
}

export const ShellLanguageSwitcher = ({
  onBack,
  onClose,
}: ShellLanguageSwitcherProps) => {
  const { t } = useTranslation("layout");
  const locale = useShellLocaleStore((state) => state.locale);
  const syncStatus = useShellLocaleStore((state) => state.syncStatus);
  const error = useShellLocaleStore((state) => state.error);
  const setLocale = useShellLocaleStore((state) => state.setLocale);
  const clearError = useShellLocaleStore((state) => state.clearError);
  const isSaving = syncStatus === "saving";
  const handleLocaleSelection = async (nextLocale: SupportedLocale) => {
    clearError();

    try {
      await setLocale(nextLocale);
      onClose();
    } catch {
      // Keep the submenu open so the user can see the existing inline error state.
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center gap-3 border-b border-border-subtle px-5 py-3 text-left text-sm font-medium text-content-primary transition-colors hover:bg-surface-secondary"
      >
        <ArrowLeft className="h-4 w-4 text-content-muted" />
        <span>{t("topbar.language.selectTitle")}</span>
        {isSaving ? (
          <span className="ml-auto inline-flex h-4 w-4 animate-spin rounded-full border-2 border-border-strong border-r-content-secondary" />
        ) : null}
      </button>

      <div className="p-2">
        <div className="mb-1 px-2 pb-2 pt-1 text-xs font-medium uppercase tracking-[0.14em] text-content-muted">
          {t("topbar.language.label")}
        </div>
        <div className="space-y-1">
          {LANGUAGE_OPTIONS.map((option) => {
            const isActive = option.value === locale;

            return (
              <button
                key={option.value}
                type="button"
                disabled={isSaving}
                aria-pressed={isActive}
                onClick={() => {
                  void handleLocaleSelection(option.value);
                }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left text-sm text-content-secondary transition-colors hover:bg-surface-secondary disabled:cursor-wait disabled:opacity-70"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center text-content-muted">
                  <Globe className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={isActive ? "font-semibold text-content-primary" : "font-medium text-content-secondary"}>
                    {t(`topbar.language.options.${option.value}.label`)}
                  </p>
                  <p className="text-xs text-content-muted">{option.detail}</p>
                </div>
                {isActive ? (
                  <Check className="h-4 w-4 text-state-success" aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>
        {error ? (
          <p className="px-2 pt-3 text-xs text-state-danger" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
};
